import { useEffect, useState } from 'react';

import type { CapValue } from '../../services/aiRateLimitConfig';

/**
 * One daily-cap value: a number of calls, or the literal `'unlimited'`.
 *
 * Shared by the per-tier caps in the AI Rate Limit config panel and the
 * per-user override on the Edit User modal, so both write the same shape and
 * enforce the same rules.
 *
 * `'unlimited'` is a checkbox rather than a typed word on purpose. watchlock's
 * `resolveCap` accepts the string case-insensitively, but it accepts it as a
 * FALLBACK — anything it fails to recognise silently becomes the tier default
 * instead of erroring. A checkbox can only ever emit the canonical lowercase
 * literal, so the one value that has to be exact never depends on typing.
 */
export function CapValueInput({
  value,
  disabled = false,
  invalid = false,
  resetKey = 0,
  numberId,
  onChange,
}: {
  value: CapValue;
  disabled?: boolean;
  invalid?: boolean;
  /** Bump to drop the local draft back to `value` (on load / after save). */
  resetKey?: number;
  numberId?: string;
  onChange: (next: CapValue) => void;
}) {
  const unlimited = value === 'unlimited';

  // Local draft so the box can be cleared and retyped freely. Without it the
  // first keystroke after clearing would be re-parsed and written back on every
  // render, and a half-typed value could not exist. Mirrors `NumberInput` in
  // SchemaConfigPanel, which solves the same problem the same way.
  const [draft, setDraft] = useState<string | null>(null);
  useEffect(() => {
    setDraft(null);
  }, [resetKey]);

  // The last number typed, kept across a trip through Unlimited so unchecking
  // restores what was there rather than an empty box.
  const [lastNumber, setLastNumber] = useState<number | undefined>(
    typeof value === 'number' ? value : undefined,
  );
  useEffect(() => {
    if (typeof value === 'number') setLastNumber(value);
  }, [value]);

  const shown =
    draft !== null ? draft : typeof value === 'number' ? String(value) : '';

  const border = invalid
    ? 'border-red-400 focus:ring-red-500 focus:border-red-500'
    : 'border-gray-300 focus:ring-brand-500 focus:border-brand-500';

  const numberDisabled = disabled || unlimited;

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <input
        id={numberId}
        type="number"
        inputMode="numeric"
        min={0}
        step={1}
        disabled={numberDisabled}
        value={unlimited ? '' : shown}
        placeholder={unlimited ? 'unlimited' : undefined}
        onChange={(e) => {
          const raw = e.target.value;
          setDraft(raw);
          if (raw === '') {
            onChange(undefined);
            return;
          }
          const n = Number(raw);
          onChange(Number.isFinite(n) ? n : undefined);
        }}
        className={`block w-28 text-sm rounded-lg py-1.5 px-2 border ${border} ${
          numberDisabled ? 'opacity-60 cursor-not-allowed bg-gray-50' : ''
        }`}
      />
      <span className="text-xs text-gray-500">per day</span>
      <label
        className={`flex items-center gap-2 ${
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
        }`}
      >
        <input
          type="checkbox"
          checked={unlimited}
          disabled={disabled}
          onChange={(e) => {
            if (e.target.checked) {
              onChange('unlimited');
            } else {
              setDraft(null);
              onChange(lastNumber);
            }
          }}
          className="rounded text-brand-600 focus:ring-brand-500"
        />
        <span className="text-sm text-gray-700">Unlimited</span>
      </label>
    </div>
  );
}
